import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export default function DiscardChangesGuard() {
  const location = useLocation();
  const dirtyRef = useRef(false);
  const allowCancelClickRef = useRef(false);
  const cancelButtonRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    dirtyRef.current = false;
    setShowPrompt(false);
    cancelButtonRef.current = null;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const markFormDirty = (event) => {
      if (event.target?.matches?.("input, select, textarea, [contenteditable='true']")) {
        dirtyRef.current = true;
      }
    };

    const interceptCancel = (event) => {
      if (allowCancelClickRef.current) return;

      const cancelButton = event.target?.closest?.("button, a, input[type='button']");
      const label = (cancelButton?.innerText || cancelButton?.value || "")
        .trim()
        .toLowerCase();
      if (!cancelButton || !/^cancel\b/.test(label) || !dirtyRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      cancelButtonRef.current = cancelButton;
      setShowPrompt(true);
    };

    document.addEventListener("input", markFormDirty, true);
    document.addEventListener("change", markFormDirty, true);
    document.addEventListener("click", interceptCancel, true);

    return () => {
      document.removeEventListener("input", markFormDirty, true);
      document.removeEventListener("change", markFormDirty, true);
      document.removeEventListener("click", interceptCancel, true);
    };
  }, []);

  const discardChanges = () => {
    document.querySelectorAll("form").forEach((form) => form.reset());
    dirtyRef.current = false;

    const cancelButton = cancelButtonRef.current;
    cancelButtonRef.current = null;
    setShowPrompt(false);

    if (cancelButton?.isConnected) {
      allowCancelClickRef.current = true;
      cancelButton.click();
      allowCancelClickRef.current = false;
    }
  };

  if (!showPrompt) return null;

  return (
    <div
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) setShowPrompt(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "rgba(0, 0, 0, 0.45)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-changes-title"
        className="bg-white rounded shadow p-4"
        style={{ width: "min(100%, 420px)" }}
      >
        <h5 id="discard-changes-title" className="mb-2">
          Discard changes?
        </h5>
        <p className="mb-4">Are you sure you want to discard the entered data and uploaded files?</p>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPrompt(false)}>
            No
          </button>
          <button type="button" className="btn btn-danger" onClick={discardChanges}>
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}
